/**
 * `UiBridgeProvider` / `useUiBridge` (W1-12 ui-kit-i18n; contract src/ui/types.ts `UiBridge`).
 *
 * Primitives that show shortcuts or run commands (IconButton, Button, Kbd, Tooltip, EmptyState, Menu,
 * ContextMenu) reach the keymap and the registry only through this bridge, so src/ui never imports app services.
 * W1-16 / the shell supply the real bridge at the app root. Without a provider, `useUiBridge` returns the INERT
 * bridge used by tests and the gallery: chords label as their own text, no bindings, every command enabled,
 * `run` does nothing, and `tr` translates through the i18n runtime.
 *
 * The provider also hosts the shared Radix tooltip provider (one delay group for the whole app).
 *
 * A bridge may also describe a command (`UiBridge.describe`: title, icon, checked state) so menus can list command
 * items without an explicit label.
 */

import { Provider as TooltipProvider } from '@radix-ui/react-tooltip';
import { createContext, useContext, useMemo } from 'react';
import type { Json, Locale, MessageRef } from '../core/util/types';
import { i18n } from '../i18n/runtime';
import { useLocale } from '../i18n/useT';
import type { UiBridge, UiBridgeProviderProps, UseUiBridge } from './types';

export const INERT_BRIDGE: UiBridge = Object.freeze({
  label: (chord: string) => chord,
  bindingsFor: () => [],
  enabled: () => true as const,
  run: (_command: string, _args?: Json) => undefined,
  tr: (ref: MessageRef) => i18n.tr(ref),
});

/** An inert bridge with some members replaced (tests, gallery, storybook-like hosts). */
export function createInertBridge(overrides: Partial<UiBridge> = {}): UiBridge {
  return { ...INERT_BRIDGE, ...overrides };
}

const BridgeContext = createContext<UiBridge>(INERT_BRIDGE);
const TooltipScope = createContext(false);

/** Tooltip delay shared by the app (ms). */
export const TOOLTIP_DELAY_MS = 500;
/** Moving from one tooltip trigger to the next within this time opens the next one at once (ms). */
export const TOOLTIP_SKIP_DELAY_MS = 300;

export function UiBridgeProvider(props: UiBridgeProviderProps) {
  return (
    <BridgeContext value={props.bridge}>
      <TooltipProvider delayDuration={TOOLTIP_DELAY_MS} skipDelayDuration={TOOLTIP_SKIP_DELAY_MS}>
        <TooltipScope value={true}>{props.children}</TooltipScope>
      </TooltipProvider>
    </BridgeContext>
  );
}

export const useUiBridge: UseUiBridge = () => useContext(BridgeContext);

/** Whether a shared tooltip provider is mounted above (Tooltip mounts its own otherwise). */
export function useHasTooltipProvider(): boolean {
  return useContext(TooltipScope);
}

/** A MessageRef translator bound to a bridge, created per locale so its identity changes with the locale. */
function translatorFor(bridge: UiBridge, _locale: Locale): (ref: MessageRef) => string {
  return (ref) => bridge.tr(ref);
}

/**
 * Translator for MessageRefs through the bridge; re-renders the caller when the locale changes.
 * Primitives use it for every MessageRef prop.
 */
export function useTr(): (ref: MessageRef) => string {
  const bridge = useContext(BridgeContext);
  const locale = useLocale();
  return useMemo(() => translatorFor(bridge, locale), [bridge, locale]);
}

/** First shortcut chord of a command, or undefined when it is unbound (or no command is given). */
export function firstChord(bridge: UiBridge, command: string | undefined): string | undefined {
  return command === undefined ? undefined : bridge.bindingsFor(command)[0];
}

/** First shortcut label of a command ("" when unbound). */
export function shortcutLabel(bridge: UiBridge, command: string | undefined): string {
  const chord = firstChord(bridge, command);
  return chord === undefined ? '' : bridge.label(chord);
}

/** The reason a command is disabled now, or null when it is enabled (or no command is given). */
export function commandDisabledReason(bridge: UiBridge, command: string | undefined): MessageRef | null {
  if (command === undefined) return null;
  const state = bridge.enabled(command);
  return state === true ? null : state;
}
